// src/helpers/syncHelpers.js
import { apiCheckProjectUpdates, apiCheckDocsUpdates } from '../api/sync';
import { debug } from './debug';

const getSyncStorageKey = userId => (userId ? `ghost_lastSync_${userId}` : null);

const uniqueDocIds = (tabs = []) => Array.from(new Set(
  (tabs || [])
    .map(tab => tab?.docId)
    .filter(docId => docId && !String(docId).startsWith('version-'))
));

const resolveVisibleDocIds = ({ tabs = [], leftActiveTabId, rightActiveTabId, splitScreen }) => {
  const leftVisibleDocId = leftActiveTabId
    ? tabs.find(t => t.id === leftActiveTabId)?.docId
    : null;
  const rightVisibleDocId = splitScreen && rightActiveTabId
    ? tabs.find(t => t.id === rightActiveTabId)?.docId
    : null;
  return [leftVisibleDocId, rightVisibleDocId].filter(Boolean);
};

const summarizeRefreshResults = (results = []) => {
  if (!Array.isArray(results) || results.length === 0) return 'none';
  return results
    .map(r => `${r.docId}:${r.applied ? 'applied' : r.unchanged ? 'unchanged' : `skipped-${r.skipped || 'unknown'}`}`)
    .join(',');
};

const saveVisibleDocs = async ({ visibleDocIds, leftPanelRef, rightPanelRef, reason }) => {
  const [leftVisibleDocId, rightVisibleDocId] = visibleDocIds;
  const results = [];

  if (leftVisibleDocId) {
    const result = await leftPanelRef?.current?.saveDoc?.(leftVisibleDocId, reason);
    if (result) results.push(result);
  }
  if (rightVisibleDocId) {
    const result = await rightPanelRef?.current?.saveDoc?.(rightVisibleDocId, reason);
    if (result) results.push(result);
  }

  return {
    results,
    savedCount: results.filter(r => r?.saved).length,
    visibleDocIds: [leftVisibleDocId, rightVisibleDocId].filter(Boolean)
  };
};

const refreshVisibleDocs = async ({ visibleDocIds, leftPanelRef, rightPanelRef, reason }) => {
  const [leftVisibleDocId, rightVisibleDocId] = visibleDocIds;
  const results = [];

  if (leftVisibleDocId) {
    const result = await leftPanelRef?.current?.getLatestDoc?.(leftVisibleDocId, reason);
    if (result) results.push(result);
  }
  if (rightVisibleDocId) {
    const result = await rightPanelRef?.current?.getLatestDoc?.(rightVisibleDocId, reason);
    if (result) results.push(result);
  }

  return {
    results,
    summary: summarizeRefreshResults(results),
    visibleDocIds: [leftVisibleDocId, rightVisibleDocId].filter(Boolean)
  };
};

/**
 * Check if project or any open docs have been updated since last sync
 * @param {number} projectId - The project ID
 * @param {Array<number>} docIds - Array of currently open doc IDs
 * @param {string} lastSyncTime - ISO timestamp of last sync (from localStorage)
 * @returns {Promise<Object>} - { projectUpdated: boolean, updatedDocs: Array<number> }
 */
export async function checkRemoteChanges(projectId, docIds, lastSyncTime) {
  if (!projectId || !lastSyncTime) {
    return { projectUpdated: false, updatedDocs: [] };
  }

  try {
    debug('checkRemoteChanges', { type: 'log' }, `projectId=${projectId} docs=${docIds?.length || 0} since=${lastSyncTime}`);

    // Check if project has been updated
    const projectUpdate = await apiCheckProjectUpdates(projectId, lastSyncTime);
    
    // Check which open docs have been updated
    const docsUpdate = docIds.length > 0 
      ? await apiCheckDocsUpdates(docIds, lastSyncTime)
      : { updatedDocs: [] };

    return {
      projectUpdated: projectUpdate.isUpdated,
      updatedDocs: docsUpdate.updatedDocs || []
    };
  } catch (err) {
    debug('checkRemoteChanges', { type: 'warn' }, `failed projectId=${projectId} error=${err?.message || err}`);
    return { projectUpdated: false, updatedDocs: [] };
  }
}

export async function runReturnSync({
  reason = 'window-focus',
  userId,
  projectId,
  tabs = [],
  leftActiveTabId,
  rightActiveTabId,
  splitScreen,
  leftPanelRef,
  rightPanelRef,
  refreshProjectTree,
  isVisible = true
} = {}) {
  if (!projectId) {
    return { skipped: 'missing-project' };
  }
  if (!isVisible) {
    return { skipped: 'document-hidden' };
  }

  const visibleDocIds = resolveVisibleDocIds({ tabs, leftActiveTabId, rightActiveTabId, splitScreen });
  const openDocIds = uniqueDocIds(tabs);
  const lastSyncTime = getLastSyncAt(userId);

  let updates = { projectUpdated: false, updatedDocs: [] };
  if (lastSyncTime) {
    updates = await checkRemoteChanges(projectId, openDocIds, lastSyncTime);
  }

  if (updates.projectUpdated && typeof refreshProjectTree === 'function') {
    try {
      await refreshProjectTree();
      debug('runReturnSync', { type: 'log' }, `reason=${reason} projectId=${projectId} projectRefresh=applied`);
    } catch (err) {
      debug('runReturnSync', { type: 'warn' }, `reason=${reason} projectId=${projectId} projectRefresh=failed error=${err?.message || err}`);
    }
  }

  const refreshResult = await refreshVisibleDocs({
    visibleDocIds,
    leftPanelRef,
    rightPanelRef,
    reason
  });

  setLastSyncAt(userId);
  debug('runReturnSync', { type: 'log' }, `reason=${reason} visibleDocs=${refreshResult.visibleDocIds.join(',') || 'none'} results=${refreshResult.summary} projectUpdated=${updates.projectUpdated} updatedDocs=${updates.updatedDocs.length}`);

  return {
    ...refreshResult,
    projectUpdated: updates.projectUpdated,
    updatedDocs: updates.updatedDocs || []
  };
}

export async function runBackgroundSync({
  reason = 'window-blur',
  userId,
  tabs = [],
  leftActiveTabId,
  rightActiveTabId,
  splitScreen,
  leftPanelRef,
  rightPanelRef
} = {}) {
  const visibleDocIds = resolveVisibleDocIds({ tabs, leftActiveTabId, rightActiveTabId, splitScreen });
  const saveResult = await saveVisibleDocs({
    visibleDocIds,
    leftPanelRef,
    rightPanelRef,
    reason
  });

  if (saveResult.savedCount > 0) {
    debug('runBackgroundSync', { type: 'log' }, `reason=${reason} visibleDocs=${saveResult.visibleDocIds.join(',') || 'none'} saved=${saveResult.savedCount}`);
  }

  return saveResult;
}

export async function runManualRefresh(args = {}) {
  return runReturnSync({ ...args, reason: args.reason || 'manual-refresh' });
}

export async function runTabSwitchSync({
  reason = 'tab-switch',
  docId,
  panelRef
} = {}) {
  if (!docId) return { skipped: 'missing-doc' };
  const result = await panelRef?.current?.getLatestDoc?.(docId, reason);
  return result || { skipped: 'missing-panel-method' };
}

/**
 * Update the last sync timestamp in localStorage
 * @param {string} userId - User ID for localStorage key
 */
export function setLastSyncAt(userId) {
  const now = new Date().toISOString();
  const key = getSyncStorageKey(userId);
  if (key) localStorage.setItem(key, now);
  return now;
}

/**
 * Get the last sync timestamp from localStorage
 * @param {string} userId - User ID for localStorage key
 * @returns {string|null} - ISO timestamp or null if never synced
 */
export function getLastSyncAt(userId) {
  const key = getSyncStorageKey(userId);
  if (!key) return null;
  return localStorage.getItem(key);
}

// Legacy aliases
export const checkForRemoteUpdates = checkRemoteChanges;
export const updateLastSyncTime = setLastSyncAt;
export const getLastSyncTime = getLastSyncAt;
